import { useState, useRef } from 'react';
import { Search, Mail, Globe, Package, Palette, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Send, Languages, Loader, Download } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function ShopifyAnalyzer() {
  const [urlInput, setUrlInput] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [generatingEmail, setGeneratingEmail] = useState({});
  const [translating, setTranslating] = useState({});
  const [editedEmails, setEditedEmails] = useState({});
  const [senderName, setSenderName] = useState('');
  const [senderService, setSenderService] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const abortRef = useRef(null);

  const analyze = async () => {
    const urls = urlInput.split('\n').map(u => u.trim()).filter(Boolean);
    if (!urls.length) return toast.error('Please paste at least one Shopify store URL');
    if (urls.length > 50) return toast.error('Maximum 50 URLs at a time');

    setAnalyzing(true);
    setResults([]);
    setProgress({ current: 0, total: urls.length });

    try {
      const token = localStorage.getItem('bulkify_token');
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const res = await fetch(`${baseUrl}/api/shopify/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ urls }),
      });

      const reader = res.body.getReader();
      abortRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'progress') setProgress({ current: data.current, total: data.total });
            if (data.type === 'result') setResults(prev => [...prev, data.result]);
            if (data.type === 'done') toast.success(`Analyzed ${data.total} stores!`);
          } catch {}
        }
      }
    } catch (err) {
      toast.error('Analysis failed: ' + err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const [painBuckets, setPainBuckets] = useState({});

  const generateEmail = async (idx) => {
    const store = results[idx];
    setGeneratingEmail(g => ({ ...g, [idx]: true }));
    try {
      const res = await api.post('/api/shopify/generate-email', { storeData: store });
      setEditedEmails(e => ({ ...e, [idx]: res.data.email }));
      if (res.data.painBucket) setPainBuckets(b => ({ ...b, [idx]: res.data.painBucket }));
      setExpanded(ex => ({ ...ex, [`email_${idx}`]: true }));
      toast.success('Email generated!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate email');
    } finally {
      setGeneratingEmail(g => ({ ...g, [idx]: false }));
    }
  };

  const translateEmail = async (idx, language) => {
    const email = editedEmails[idx];
    if (!email) return;
    setTranslating(t => ({ ...t, [idx]: true }));
    try {
      const res = await api.post('/api/shopify/translate', {
        email,
        language,
        storeName: results[idx].storeName,
      });
      setEditedEmails(e => ({ ...e, [idx]: res.data.translated }));
      toast.success(`Translated to ${language}!`);
    } catch (err) {
      toast.error('Translation failed');
    } finally {
      setTranslating(t => ({ ...t, [idx]: false }));
    }
  };

  const addToLeads = async () => {
    const storesWithEmail = results.filter(r => r.email && editedEmails[results.indexOf(r)]);
    if (!storesWithEmail.length) return toast.error('Generate emails first before saving to leads');
    toast.success(`${storesWithEmail.length} stores ready — save to leads coming soon!`);
  };

  const downloadCSV = () => {
    const headers = ['Store Name', 'URL', 'Email', 'Country', 'Theme', 'Theme Type', 'Products', 'Pain Points', 'Generated Email'];
    const rows = results.map((r, i) => [
      r.storeName, r.url, r.email, r.country, r.themeName, r.themeType,
      r.productCount, r.painPoints.map(p => p.issue).join('; '),
      editedEmails[i] || '',
    ].map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shopify-analysis-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusColor = (store) => {
    if (store.error) return 'var(--red)';
    if (store.painPoints.length >= 5) return 'var(--red)';
    if (store.painPoints.length >= 3) return 'var(--amber)';
    return 'var(--green)';
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.4px' }}>Shopify Store Analyzer</h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>
          Paste Shopify store URLs → crawl & detect issues → AI generates personalized outreach emails
        </p>
      </div>

      {/* URL input */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Paste Shopify store URLs</div>
        <textarea className="input" rows={6} placeholder={'https://store1.myshopify.com\nhttps://store2.com\nhttps://store3.myshopify.com\n\nOne URL per line — max 50 at a time'}
          value={urlInput} onChange={e => setUrlInput(e.target.value)} style={{ resize: 'vertical', lineHeight: 1.6 }} disabled={analyzing} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>
            {urlInput.split('\n').filter(u => u.trim()).length} URLs entered
          </span>
          <button className="btn btn-primary" onClick={analyze} disabled={analyzing || !urlInput.trim()}>
            {analyzing ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Analyzing {progress.current}/{progress.total}…</> : <><Search size={14} /> Analyze stores</>}
          </button>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{results.length} stores analyzed</div>
            <button className="btn btn-ghost btn-sm" onClick={downloadCSV}><Download size={13} /> Download CSV</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {results.map((store, idx) => (
              <div key={idx} className="card" style={{ borderLeft: `3px solid ${statusColor(store)}` }}>
                {/* Store header */}
                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 42, height: 42, borderRadius: '50%', background: `${statusColor(store)}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Globe size={18} color={statusColor(store)} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{store.storeName || store.url}</span>
                      {store.country && <span className="badge badge-gray">{store.country}</span>}
                      {store.themeType && <span className={`badge ${store.themeType === 'Free' ? 'badge-amber' : 'badge-green'}`}>{store.themeType} theme</span>}
                      {store.error && <span className="badge badge-red">Error</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {store.email && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} color="var(--green)" /> {store.email}</span>}
                      {store.productCount > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Package size={11} /> {store.productCount} products</span>}
                      {store.themeName && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Palette size={11} /> {store.themeName}</span>}
                      {!store.email && !store.error && <span style={{ color: 'var(--red)' }}>No email found</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(ex => ({ ...ex, [idx]: !ex[idx] }))}>
                      {expanded[idx] ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      {store.painPoints.length} issues
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => generateEmail(idx)} disabled={generatingEmail[idx] || !store.email}>
                      {generatingEmail[idx] ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</> : <><Mail size={12} /> Generate email</>}
                    </button>
                  </div>
                </div>

                {/* Pain points */}
                {expanded[idx] && (
                  <div style={{ padding: '0 20px 16px', borderTop: '1px solid var(--border)' }}>
                    {store.error ? (
                      <div style={{ padding: '12px 0', color: 'var(--red)', fontSize: 13 }}>⚠️ {store.error}</div>
                    ) : (
                      <>
                        {store.positives.length > 0 && (
                          <div style={{ marginTop: 14, marginBottom: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--green)', marginBottom: 8 }}>✅ What they're doing well</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {store.positives.map((p, i) => (
                                <span key={i} style={{ fontSize: 11, padding: '3px 10px', background: 'rgba(34,197,94,0.1)', color: 'var(--green)', borderRadius: 20, border: '1px solid rgba(34,197,94,0.2)' }}>{p}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {store.painPoints.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--red)', marginBottom: 8 }}>⚠️ Issues found</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {store.painPoints.map((p, i) => (
                                <div key={i} style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
                                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <AlertTriangle size={12} color="var(--amber)" /> {p.issue}
                                  </div>
                                  <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>💡 {p.fix}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Generated email */}
                {editedEmails[idx] && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Mail size={14} color="var(--blue)" /> Generated outreach email
                        {painBuckets[idx] && (
                          <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, fontWeight: 600,
                            background: painBuckets[idx] === 'TRUST' ? 'rgba(239,68,68,0.15)' : painBuckets[idx] === 'TRAFFIC' ? 'rgba(59,130,246,0.15)' : 'rgba(168,85,247,0.15)',
                            color: painBuckets[idx] === 'TRUST' ? 'var(--red)' : painBuckets[idx] === 'TRAFFIC' ? 'var(--blue)' : '#a855f7',
                            border: `1px solid ${painBuckets[idx] === 'TRUST' ? 'rgba(239,68,68,0.3)' : painBuckets[idx] === 'TRAFFIC' ? 'rgba(59,130,246,0.3)' : 'rgba(168,85,247,0.3)'}`,
                          }}>
                            {painBuckets[idx]} bucket
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {store.languages && store.languages.length > 0 && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            {store.languages.map(lang => (
                              <button key={lang} className="btn btn-ghost btn-sm" onClick={() => translateEmail(idx, lang)} disabled={translating[idx]}
                                style={{ fontSize: 11 }}>
                                {translating[idx] ? <Loader size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Languages size={11} />}
                                {lang}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <textarea className="input" rows={8} value={editedEmails[idx]}
                      onChange={e => setEditedEmails(em => ({ ...em, [idx]: e.target.value }))}
                      style={{ resize: 'vertical', lineHeight: 1.7, fontSize: 13 }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className="btn btn-primary btn-sm" onClick={() => {
                        navigator.clipboard.writeText(editedEmails[idx]);
                        toast.success('Copied to clipboard!');
                      }}>
                        Copy email
                      </button>
                    {store.email && (
                      <span style={{ fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }}>
                        Sending to: <strong style={{ color: 'var(--text2)' }}>{store.email}</strong>
                        {store.guessedOwnerName && (
                          <span style={{ marginLeft: 8, color: 'var(--green)' }}>· Name detected: <strong>{store.guessedOwnerName}</strong></span>
                        )}
                        {!store.guessedOwnerName && (
                          <span style={{ marginLeft: 8, color: 'var(--amber)' }}>· Replace [name] manually</span>
                        )}
                      </span>
                    )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
